import { request } from './request';

export interface ContractData {
  contract_id: string;
  bid_id: string;
  job_id: string | null;
  client_signature: boolean | null;
  client_signed_at: string | null;
  filmmaker_signature: boolean | null;
  filmmaker_signed_at: string | null;
  custom_terms?: string;
}

export interface ContractDetails {
  contract?: ContractData;
  clientName: string;
  filmmakerName: string;
  jobTitle: string;
  bidData: any;
  jobData: any;
  locationData: any;
  clientData: any;
}

// Client wallet contracts interfaces
export interface ContractWithBids {
  contract_id: string;
  client_signature: boolean | null;
  filmmaker_signature: boolean | null;
  bid_id: string;
  bids?: {
    bid_amount: number;
    job_id: string;
    client_project_jobs: {
      title: string;
      client_project_id: string;
      client_project: {
        client_id: string;
      };
    };
  };
  isPaidJob?: boolean; // Flag to indicate this is a paid job (payment made)
}

export interface WalletWithContracts {
  clientId: string;
  walletBalance: number;
  signedContracts: ContractWithBids[];
  totalCommittedAmount: number;
  availableBalance: number;
}

export const contractsApi = {
  // Contract CRUD operations
  async getContractByBidId(bidId: string): Promise<ContractData | null> {
    try {
      return await request(`/contracts/bid/${bidId}`, {
        method: 'GET'
      });
    } catch (error: any) {
      // Return null if contract not found
      if (error.message?.includes('not found')) return null;
      throw error;
    }
  },

  async createContract(contractData: {
    bid_id: string;
    job_id: string;
    client_signature: boolean;
    client_signed_at?: string;
    filmmaker_signature: boolean;
    filmmaker_signed_at?: string;
    custom_terms?: string;
  }): Promise<ContractData> {
    return request('/contracts', {
      method: 'POST',
      body: JSON.stringify(contractData)
    });
  },

  async updateContract(contractId: string, updates: Partial<ContractData>): Promise<ContractData> {
    return request(`/contracts/${contractId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  },

  async deleteContract(contractId: string): Promise<{ ok: boolean }> {
    return request(`/contracts/${contractId}`, {
      method: 'DELETE'
    });
  },

  // Contract signing operations
  async signContractAsClient(params: {
    bidId: string;
    jobId: string;
    customTerms?: string;
  }): Promise<ContractData> {
    return request('/contracts/sign-client', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  },

  async signContractAsFilmmaker(params: {
    bidId: string;
    contractId: string;
  }): Promise<ContractData> {
    return request('/contracts/sign-filmmaker', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  },

  async rescindContract(params: {
    contractId: string;
    bidId: string;
  }): Promise<{ ok: boolean }> {
    return request('/contracts/rescind', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  },

  // Contract details with related data
  async getContractDetails(bidId: string): Promise<ContractDetails> {
    return request(`/contracts/details/${bidId}`, {
      method: 'GET'
    });
  },

  // Project and client information for contracts
  async getProjectClientInfo(projectId: string): Promise<{
    client_id: string;
    company_name?: string;
    user_id: string;
  }> {
    return request(`/contracts/projects/${projectId}/client`, {
      method: 'GET'
    });
  },

  async getFilmmakerUserInfo(filmmakerId: string): Promise<{
    user_id: string;
    first_name: string;
    last_name: string;
  }> {
    return request(`/contracts/filmmakers/${filmmakerId}/user`, {
      method: 'GET'
    });
  },

  // Client wallet contracts with complex calculations
  async getClientWalletWithContracts(clientId: string): Promise<WalletWithContracts> {
    return request(`/contracts/client/${clientId}/wallet-contracts`, {
      method: 'GET'
    });
  },
}; 